import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { APP_CFG } from '@/configs';
import { CAMPAIGN_CFG } from '@/configs/campaign-config';
import { SuiIcon } from '@/components/icons/SuiIcon';
import { ComponentType } from 'react';
import { UsdcIcon } from '@/components/icons/UsdcIcon';

const CURRENCY_STORAGE_KEY = 'suigar.currency.info';

export interface Currency {
	symbol: string;
	name: string;
	type: string;
	icon: ComponentType<{ className?: string }>;
	decimals: number;
}

const BASE_SUPPORTED_CURRENCIES: Currency[] = [
	{
		symbol: 'SUI',
		name: 'Sui',
		type: APP_CFG.SUI_COIN_TYPE,
		icon: SuiIcon,
		decimals: 9,
	},
	// Conditionally include USDC only if configured for the current env
	...(APP_CFG.USDC_COIN_TYPE
		? [
				{
					symbol: 'USDC',
					name: 'USDC',
					type: APP_CFG.USDC_COIN_TYPE,
					icon: UsdcIcon,
					decimals: 6,
				} as Currency,
			]
		: []),
	...(APP_CFG.USDCFLOWX_COIN_TYPE
		? [
				{
					symbol: 'FXUSDC',
					name: 'FXUSDC',
					type: APP_CFG.USDCFLOWX_COIN_TYPE,
					icon: UsdcIcon,
					decimals: 6,
				} as Currency,
			]
		: []),
];

const allowedSymbols = new Set(
	(CAMPAIGN_CFG.features.supportedCurrencies?.length ? CAMPAIGN_CFG.features.supportedCurrencies : ['SUI']).map((symbol) => symbol.toUpperCase()),
);

export const CONFIGURED_CURRENCIES: Currency[] = BASE_SUPPORTED_CURRENCIES;

export const SUPPORTED_CURRENCIES: Currency[] = BASE_SUPPORTED_CURRENCIES.filter((currency) => allowedSymbols.has(currency.symbol.toUpperCase()));

if (SUPPORTED_CURRENCIES.length === 0) {
	SUPPORTED_CURRENCIES.push(BASE_SUPPORTED_CURRENCIES[0]);
}

interface CurrencyState {
	selectedCurrency: Currency;
	setSelectedCurrency: (currency: Currency) => void;
}

export const useCurrencyStore = create<CurrencyState>()(
	persist(
		(set) => ({
			selectedCurrency: SUPPORTED_CURRENCIES[0], // Default to SUI
			setSelectedCurrency: (currency) => set({ selectedCurrency: currency }),
		}),
		{
			name: CURRENCY_STORAGE_KEY,
			storage: {
				getItem: (name) => {
					const str = localStorage.getItem(name);
					if (!str) return null;

					const { state } = JSON.parse(str);
					// In older versions, the whole object was stored. Now, only the type is.
					const currencyType = typeof state.selectedCurrency === 'string' ? state.selectedCurrency : state.selectedCurrency.type;

					const currency = SUPPORTED_CURRENCIES.find((c) => c.type === currencyType);

					return {
						state: {
							selectedCurrency: currency || SUPPORTED_CURRENCIES[0],
						},
					};
				},
				setItem: (name, newValue) => {
					// Store only the currency type, not the whole object
					const { state, version } = newValue;
					const stateToPersist = {
						state: {
							selectedCurrency: state.selectedCurrency.type,
						},
						version,
					};
					localStorage.setItem(name, JSON.stringify(stateToPersist));
				},
				removeItem: (name) => localStorage.removeItem(name),
			},
		},
	),
);
